// Windowed rendering for the mobile product list.
//
// Hand-rolled rather than pulling in react-window / @tanstack/react-virtual:
// the app ships three runtime dependencies (react, react-dom, leaflet) and a
// 345KB bundle, and this list has a shape those libraries' generality is
// aimed at avoiding -- see below.
//
// The shape: collapsed rows are a UNIFORM height (.m-row-name is nowrap +
// ellipsis, so a name never wraps; measured 43px across every row of every
// fixture), and the only exceptions are rows the user has expanded, which the
// view already tracks by key. So offsets are analytic rather than requiring a
// measured-height cache over the whole list:
//
//   offset(i) = i * rowH + (extra height of every expanded row before i)
//
// with the expanded set normally empty and never more than a handful.

import { useCallback, useEffect, useRef, useState } from "react";

// Rendered above and below the viewport so a flick doesn't outrun React.
const OVERSCAN = 8;

// Starting guess only. The real height is measured from the first rendered
// row on mount (see measureRow) so a padding or font change in styles.css
// can't silently desync this module from what the browser actually lays out.
const ROW_H_FALLBACK = 43;

export interface VirtualWindow {
  /** First row index to render (inclusive). */
  start: number;
  /** Last row index to render (exclusive). */
  end: number;
  /** Spacer height standing in for rows before `start`. */
  padTop: number;
  /** Spacer height standing in for rows after `end`. */
  padBottom: number;
  /** Callback ref for a rendered row: measures collapsed and expanded heights. */
  measureRow: (key: string, el: HTMLElement | null, isExpanded: boolean) => void;
  /**
   * First row actually at or below the top of the viewport (no overscan) --
   * the row the reader is looking at. The scrubber names it in its bubble.
   */
  firstVisible: number;
  /** How far down the list we are, 0..1. Drives the scrubber's thumb. */
  scrollFraction: number;
  /** True once the list is long enough that scrolling it is real work. */
  overflows: boolean;
}

/**
 * @param count      total rows in the (filtered, sorted) list
 * @param container  the scrolling element (.m-list)
 * @param expanded   keys of rows currently showing a detail panel
 * @param keyAt      index -> row key, for attributing measured extra heights
 * @param deps       changes that invalidate measurements (category, filters)
 */
export function useVirtualRows(
  count: number,
  container: React.RefObject<HTMLElement | null>,
  expanded: Set<string>,
  keyAt: (i: number) => string,
  enabled: boolean,
): VirtualWindow {
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const [rowH, setRowH] = useState(ROW_H_FALLBACK);
  // key -> px beyond a collapsed row. Only expanded rows appear here; a row
  // can only be expanded while on screen, so it is always measured before its
  // height matters to anyone else's offset.
  const extras = useRef<Map<string, number>>(new Map());
  const [extrasVersion, setExtrasVersion] = useState(0);

  // Track the container's scroll position and height.
  useEffect(() => {
    const el = container.current;
    if (!el || !enabled) return;
    const sync = () => {
      setScrollTop(el.scrollTop);
      setViewportH(el.clientHeight);
    };
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", sync);
      ro.disconnect();
    };
  }, [container, enabled]);

  const measureRow = useCallback(
    (key: string, el: HTMLElement | null, isExpanded: boolean) => {
      if (!el) return;
      const h = el.getBoundingClientRect().height;
      if (h <= 0) return;
      if (!isExpanded) {
        // Collapsed rows are all the same height; the first one to report
        // establishes it for the whole list.
        setRowH((prev) => (Math.abs(prev - h) > 1 ? h : prev));
        if (extras.current.delete(key)) setExtrasVersion((v) => v + 1);
        return;
      }
      const extra = Math.max(0, h - rowH);
      if (Math.abs((extras.current.get(key) ?? 0) - extra) > 1) {
        extras.current.set(key, extra);
        setExtrasVersion((v) => v + 1);
      }
    },
    [rowH],
  );

  if (!enabled || count === 0) {
    return {
      start: 0,
      end: count,
      padTop: 0,
      padBottom: 0,
      measureRow,
      firstVisible: 0,
      scrollFraction: 0,
      overflows: false,
    };
  }

  // Expanded rows, as (index, extra) sorted by index. Normally empty.
  //
  // Gated on `expanded` rather than pruned when a row collapses: a stale
  // measurement for a row that is no longer open is simply never consulted,
  // which keeps this a pure render-time derivation instead of an effect that
  // writes state (and cascades a render) every time the set changes.
  void extrasVersion; // re-derive when a measurement lands
  const marks: { index: number; extra: number }[] = [];
  if (extras.current.size && expanded.size) {
    for (let i = 0; i < count; i++) {
      const key = keyAt(i);
      if (!expanded.has(key)) continue;
      const extra = extras.current.get(key);
      if (extra) marks.push({ index: i, extra });
    }
  }
  const extraBefore = (i: number) => {
    let sum = 0;
    for (const m of marks) {
      if (m.index >= i) break;
      sum += m.extra;
    }
    return sum;
  };
  const offsetOf = (i: number) => i * rowH + extraBefore(i);
  const totalH = count * rowH + marks.reduce((s, m) => s + m.extra, 0);

  // offsetOf is monotonic, so binary search finds the first row whose bottom
  // edge is past the top of the viewport.
  const findIndex = (y: number) => {
    let lo = 0;
    let hi = count;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (offsetOf(mid + 1) <= y) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  const firstVisible = findIndex(scrollTop);
  const lastVisible = findIndex(scrollTop + Math.max(viewportH, rowH));
  const start = Math.max(0, firstVisible - OVERSCAN);
  const end = Math.min(count, lastVisible + 1 + OVERSCAN);

  const scrollable = Math.max(0, totalH - viewportH);
  return {
    start,
    end,
    padTop: offsetOf(start),
    padBottom: Math.max(0, totalH - offsetOf(end)),
    measureRow,
    firstVisible,
    scrollFraction: scrollable > 0 ? Math.min(1, Math.max(0, scrollTop / scrollable)) : 0,
    // A couple of screens is where flicking stops being reasonable and a
    // jump control starts earning its keep.
    overflows: totalH > viewportH * 2.5,
  };
}
